import { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuItem,
  MenuItems,
  Popover,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import Searchbar from "../Search";
import CustomTooltip from "./CustomTooltip";
import CustomButton from "../CustomButton";
import { useLocation } from "react-router-dom";
import {
  generateV2ServiceCategoryOptions,
  isNewServiceCategory,
  getAncestors,
  getDescendants,
} from "../../utils/serviceCategoryV2Mapping";
import {
  getFilterCheckboxHandlerArgs,
  getSelectedFilterCounts,
  hasAnySelectedFilterIds,
  isFilterOptionChecked,
  resolveFilterOptionId,
  resetSelectedFilterIds,
} from "../../utils/filtersDropdownOptionUtils";

const sortOptions = [
  { name: "Most Popular", href: "#", current: true },
  { name: "Best Rating", href: "#", current: false },
  { name: "Newest", href: "#", current: false },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function FIltersDropdown({
  rfis,
  providers,
  setSelectedIds,
  selectedIds,
  filters,
  showGroupLabel = false,
  onFilterSectionOpen,
  extraDesktopFilters = null,
  extraMobileSections = [],
  hasExtraFiltersActive = false,
  onClearExtraFilters,
  useLargeScreenControls = false,
}) {
  const [open, setOpen] = useState(false);
  const [filteredData, setFilteredData] = useState(filters);
  const [searchQuery, setSearchQuery] = useState("");
  const [v2SearchQuery, setV2SearchQuery] = useState("");
  // Derive from selectedIds so sticky/hydration shows badges + Remove Filters
  // the same as a live user selection (not only after checkbox clicks).
  const selectedCount = useMemo(
    () => getSelectedFilterCounts(selectedIds),
    [selectedIds]
  );
  const showRemoveFiltersButton =
    hasAnySelectedFilterIds(selectedIds) || hasExtraFiltersActive;

  // Build V2 grouped service category options from API data
  const v2ServiceOptions = useMemo(() => {
    return generateV2ServiceCategoryOptions(rfis || []);
  }, [rfis]);

  // Filter V2 options by search
  const filteredV2Options = useMemo(() => {
    if (!v2ServiceOptions) return [];
    if (!v2SearchQuery.trim()) return v2ServiceOptions;
    const q = v2SearchQuery.toLowerCase();
    const matchingGroups = new Set();
    v2ServiceOptions.forEach((opt) => {
      if (!opt.isGroupHeader && opt.label.toLowerCase().includes(q)) {
        if (opt.groupName) matchingGroups.add(opt.groupName);
      }
    });
    return v2ServiceOptions.filter((opt) => {
      if (opt.isGroupHeader) return matchingGroups.has(opt.label);
      return opt.label.toLowerCase().includes(q);
    });
  }, [v2ServiceOptions, v2SearchQuery]);

  // Name→ID lookup for V2 options
  const v2NameToId = useMemo(() => {
    const map = {};
    (v2ServiceOptions || []).forEach((o) => {
      if (!o.isGroupHeader && o.rfiId) map[o.value] = o.rfiId;
    });
    return map;
  }, [v2ServiceOptions]);

  // V2 checkbox handler — stores rfi_id in selectedIds.services
  const handleV2CheckboxChange = (optionValue, checked) => {
    setSelectedIds((prev) => {
      let currentIds = prev.services || [];

      if (optionValue.startsWith("__group__")) {
        const groupName = optionValue.replace("__group__", "");
        const memberNames =
          v2ServiceOptions
            ?.filter((o) => !o.isGroupHeader && o.groupName === groupName)
            ?.map((o) => o.value) || [];
        const memberIds = memberNames.map((n) => v2NameToId[n]).filter(Boolean);
        currentIds = checked
          ? [...new Set([...currentIds, ...memberIds])]
          : currentIds.filter((v) => !new Set(memberIds).has(v));
      } else if (checked) {
        const namesToAdd = [
          optionValue,
          ...getDescendants(optionValue),
          ...getAncestors(optionValue),
        ];
        const idsToAdd = namesToAdd.map((n) => v2NameToId[n]).filter(Boolean);
        currentIds = [...new Set([...currentIds, ...idsToAdd])];
      } else {
        const idToRemove = v2NameToId[optionValue];
        currentIds = currentIds.filter((v) => v !== idToRemove);
      }
      return { ...prev, services: currentIds };
    });
  };

  // Sync filteredData when filters change (e.g. when Services/Templates load asynchronously)
  useEffect(() => {
    if (searchQuery.length <= 2) {
      setFilteredData(filters);
    } else {
      // Re-apply search on the new filters
      const searchValue = searchQuery.toLowerCase();
      const filtered = filters.map((filter) => {
        if (
          filter.name === "Vendor" ||
          filter.name === "Services" ||
          filter.name === "Scope" ||
          filter.name === "Auditor" ||
          filter.name === "Sponsor" ||
          filter.name === "Templates"
        ) {
          const filteredOptions =
            filter.options?.filter((option) => {
              const toBeFiltered =
                option?.label ||
                option?.name ||
                option?.services ||
                option?.rfi_name ||
                option?.vqa_auditor_name;
              return (
                toBeFiltered && toBeFiltered.toLowerCase().includes(searchValue)
              );
            }) || [];
          return { ...filter, options: filteredOptions };
        }
        return filter;
      });
      setFilteredData(filtered);
    }
  }, [filters, searchQuery]);

  const handleRemoveFilters = () => {
    setSelectedIds(resetSelectedFilterIds(selectedIds));
    setFilteredData(filters);
    setSearchQuery("");
    onClearExtraFilters?.();
  };
  const location = useLocation();
  const currentPath = location.pathname;
  const addMarginRight = true;
  const handleSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchQuery(searchValue);

    if (searchValue.length > 2) {
      // Local filtering for all sections, including Templates
      const filtered = filters.map((filter) => {
        if (
          filter.name === "Vendor" ||
          filter.name === "Services" ||
          filter.name === "Scope" ||
          filter.name === "Auditor" ||
          filter.name === "Sponsor" ||
          filter.name === "Templates"
        ) {
          const filteredOptions =
            filter.options?.filter((option) => {
              const toBeFiltered =
                option?.label ||
                option?.name ||
                option?.services ||
                option?.rfi_name ||
                option?.vqa_auditor_name;
              return (
                toBeFiltered && toBeFiltered.toLowerCase().includes(searchValue)
              );
            }) || [];
          return {
            ...filter,
            options: filteredOptions,
          };
        }
        return filter;
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(filters);
    }
  };

  const handlePopOverClose = () => {
    setSearchQuery("");
    setFilteredData(filters);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setFilteredData(filters);
  };

  const handleCheckboxChange =
    (docId, statusID, id, rfi_id, vqa_auditor_id, hash_id, service_name) =>
    (event) => {
      const sectionId = event.target.name.split("[]")[0];
      const optionId = resolveFilterOptionId({
        currentPath,
        sectionId,
        docId,
        statusID,
        id,
        rfi_id,
        vqa_auditor_id,
        hash_id,
        service_name,
      });

      const isChecked = event.target.checked;

      setSelectedIds((prevSelectedIds) => ({
        ...prevSelectedIds,
        [sectionId]: isChecked
          ? [...(prevSelectedIds[sectionId] || []), optionId]
          : (prevSelectedIds[sectionId] || []).filter(
              (sid) => sid !== optionId
            ),
      }));
    };

  return (
    <div className="bg-white">
      {/* Mobile filter dialog */}
      <Dialog
        open={open}
        onClose={setOpen}
        className={classNames(
          "relative z-40",
          useLargeScreenControls ? "lg:hidden" : "xl:hidden"
        )}
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black bg-opacity-25 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 z-40 flex">
          <DialogPanel
            transition
            className="relative ml-auto flex h-full w-full max-w-xs transform flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl transition duration-300 ease-in-out data-[closed]:translate-x-full"
          >
            <div className="flex items-center justify-between px-4">
              <h2 className="text-lg font-medium text-gray-900">Filters</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>

            {/* Filters */}
            <form className="mt-4">
              {filteredData?.map((section) => (
                <Disclosure
                  key={section.name}
                  as="div"
                  className="border-t border-gray-200 px-4 py-6"
                >
                  <h3 className="-mx-2 -my-3 flow-root">
                    <DisclosureButton
                      className="group flex w-full items-center justify-between bg-white px-2 py-3 text-sm text-gray-400"
                      onClick={() => onFilterSectionOpen?.(section.id)}
                    >
                      <span className="font-medium text-gray-900">
                        {section.name}
                      </span>
                      <span className="ml-6 flex items-center">
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="h-5 w-5 rotate-0 transform group-data-[open]:-rotate-180"
                        />
                      </span>
                    </DisclosureButton>
                  </h3>
                  <DisclosurePanel className="pt-6">
                    {/* ── V2 Services mobile ── */}
                    {section.id === "services" && v2ServiceOptions ? (
                      <div className="space-y-2.5 max-h-[26rem] overflow-y-auto pb-1 pr-1">
                        {filteredV2Options.map((option, optionIdx) => {
                          if (option.isGroupHeader) {
                            const groupMembers = option.groupMembers || [];
                            const selectedInGroup = groupMembers.filter((m) => selectedIds.services?.includes(v2NameToId[m]));
                            const allSelected = groupMembers.length > 0 && selectedInGroup.length === groupMembers.length;
                            const someSelected = selectedInGroup.length > 0 && !allSelected;
                            return (
                              <div key={`m-grp-${optionIdx}`} className="pt-3 pb-1 first:pt-0">
                                <span
                                  className={`text-[11px] font-semibold uppercase tracking-wider select-none cursor-pointer transition-colors ${allSelected || someSelected ? "text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}
                                  onClick={() => handleV2CheckboxChange(option.value, !allSelected)}
                                >
                                  {option.label}
                                </span>
                              </div>
                            );
                          }
                          const showNew = isNewServiceCategory(option.value);
                          const indentPx = (option.depth || 0) * 16;
                          const isChecked = selectedIds.services?.includes(option.rfiId) || false;
                          return (
                            <div key={`m-sc-${optionIdx}`} className="flex items-start gap-3" style={{ paddingLeft: `${indentPx}px` }}>
                              <input
                                value={option.value}
                                checked={isChecked}
                                onChange={(e) => handleV2CheckboxChange(option.value, e.target.checked)}
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mt-0.5"
                              />
                              <label className="text-sm text-gray-500 cursor-pointer flex-1">
                                {option.label}
                                {showNew && (
                                  <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-violet-600 ring-1 ring-inset ring-violet-400/60 align-middle">
                                    New
                                  </span>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                    <div className="space-y-6">
                      {(section.name === "Vendor" ||
                        section.name === "Services" ||
                        section.name === "Scope" ||
                        section.name === "Auditor" ||
                        section.name === "Sponsor") && (
                        <div className="mb-2">
                          <Searchbar
                            placeholder="Search here..."
                            isFilterSearch={false}
                            isSearchIcon={true}
                            width="w-full"
                            marginRight="mr-2"
                            mainSearch={true}
                            toolbar={true}
                            onChange={handleSearch} // Trigger search on input change
                            handleClearSearch={handleClearSearch}
                          />
                        </div>
                      )}
                      {section?.options?.map((option, optionIdx) => (
                        <div
                          key={option.value || optionIdx}
                          className="flex items-start gap-3"
                        >
                          <input
                            value={
                              option?.value || option?.id || option?.rfi_id
                            }
                            checked={isFilterOptionChecked({
                              currentPath,
                              sectionId: section.id,
                              selectedIds,
                              option,
                            })}
                            id={`filter-${section.id}-${optionIdx}`}
                            name={`${section.id}[]`}
                            type="checkbox"
                            onChange={handleCheckboxChange(
                              ...getFilterCheckboxHandlerArgs(option)
                            )}
                            className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label
                            htmlFor={`filter-${section.id}-${optionIdx}`}
                            className="min-w-0 flex-1 break-words text-sm text-gray-500 cursor-pointer"
                          >
                            <CustomTooltip cutOffAfter={30} sliceLength={30}>
                              {option?.label ||
                                option?.name ||
                                option?.rfi_name ||
                                option?.vqa_auditor_name}
                            </CustomTooltip>
                          </label>
                        </div>
                      ))}
                    </div>
                    )}
                  </DisclosurePanel>
                </Disclosure>
              ))}
              {extraMobileSections?.map((section) => (
                <Disclosure
                  key={section.id}
                  as="div"
                  className="border-t border-gray-200 px-4 py-6"
                >
                  <h3 className="-mx-2 -my-3 flow-root">
                    <DisclosureButton className="group flex w-full items-center justify-between bg-white px-2 py-3 text-sm text-gray-400">
                      <span className="font-medium text-gray-900">
                        {section.name}
                      </span>
                      <span className="ml-6 flex items-center">
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="h-5 w-5 rotate-0 transform group-data-[open]:-rotate-180"
                        />
                      </span>
                    </DisclosureButton>
                  </h3>
                  <DisclosurePanel className="pt-6">
                    {section.content}
                  </DisclosurePanel>
                </Disclosure>
              ))}
            </form>
            <div style={{ marginRight: addMarginRight ? "0px" : "" }}>
              {showRemoveFiltersButton ? (
              <CustomButton
                color="bg-indigo-600"
                size="text-base"
                weight="font-normal"
                height="h-9"
                width="w-full" // Full width for mobile
                textColor="text-white"
                text="Remove Filters"
                onClick={handleRemoveFilters}
              />
              ) : null}
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Filters */}
      <section aria-labelledby="filter-heading" className="relative z-40">
        <div className={classNames("bg-white", useLargeScreenControls ? "py-1" : "py-4")}>
          <div className="mx-auto flex items-center justify-between px-2">
            <Menu as="div" className="relative inline-block text-left">
              <MenuItems
                transition
                portal
                anchor="bottom start"
                className="z-[100] w-40 origin-top-left rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <div className="py-1">
                  {sortOptions.map((option) => (
                    <MenuItem key={option.name}>
                      <a
                        href={option.href}
                        className={classNames(
                          option.current
                            ? "font-medium text-gray-900"
                            : "text-gray-500",
                          "block px-4 py-2 text-sm data-[focus]:bg-gray-100"
                        )}
                      >
                        {option.name}
                      </a>
                    </MenuItem>
                  ))}
                </div>
              </MenuItems>
            </Menu>

            <div
              className={classNames(
                "flex items-center",
                useLargeScreenControls ? "lg:hidden" : "xl:hidden"
              )}
            >
              {showGroupLabel ? (
                <span className="mr-2 shrink-0 text-sm font-semibold text-indigo-600">
                  Filters:
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-block text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Filters
              </button>
            </div>

            <div
              className={classNames(
                useLargeScreenControls
                  ? "hidden lg:flex lg:items-center"
                  : "hidden xl:flex xl:items-center",
                showGroupLabel && "gap-2"
              )}
            >
              {showGroupLabel ? (
                <span className="shrink-0 text-sm font-semibold text-indigo-600">
                  Filters:
                </span>
              ) : null}
              {showGroupLabel && showRemoveFiltersButton && (
                <div className="shrink-0">
                  <CustomButton
                    color="bg-indigo-600"
                    size="text-base"
                    weight="font-normal"
                    height="h-9"
                    width="w-24"
                    textColor="text-white"
                    text="Remove Filters"
                    fontSize="h-6 text-[12px]"
                    onClick={handleRemoveFilters}
                  />
                </div>
              )}
              <div className="flow-root min-w-0">
                <PopoverGroup
                  className={classNames(
                    "flex items-center divide-x divide-gray-200",
                    showGroupLabel ? "mx-0" : "-mx-4 w-54"
                  )}
                >
                  {!showGroupLabel && showRemoveFiltersButton && (
                    <div style={{ marginRight: addMarginRight ? "30px" : "" }}>
                      <CustomButton
                        color="bg-indigo-600"
                        size="text-base"
                        weight="font-normal"
                        height="h-9"
                        width="w-24"
                        textColor="text-white"
                        text="Remove Filters"
                        fontSize="h-6 text-[12px]"
                        onClick={handleRemoveFilters}
                      />
                    </div>
                  )}
                  {filteredData?.map((section, sectionIdx) => (
                    <Popover
                      className={classNames(
                        "relative inline-block text-left",
                        useLargeScreenControls ? "px-2 sm:px-3" : "px-4"
                      )}
                      key={section.id}
                    >
                      {({ close }) => (
                        <>
                          <Popover.Button
                            className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
                            onClick={() => {
                              close();
                              handlePopOverClose();
                              onFilterSectionOpen?.(section.id);
                            }}
                          >
                            <span>{section.name}</span>
                            {selectedCount[section.id] > 0 && (
                              <span
                                key={section.id}
                                className="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700"
                              >
                                {<p>{selectedCount[section.id]}</p>}
                              </span>
                            )}

                            <ChevronDownIcon
                              aria-hidden="true"
                              className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                            />
                          </Popover.Button>

                          <PopoverPanel
                            transition
                            portal
                            anchor="bottom end"
                            className="w-80 max-h-96 overflow-y-auto overflow-x-hidden z-[100] origin-top-right rounded-md bg-white p-4 shadow-2xl ring-1 ring-black ring-opacity-5 transition focus:outline-none"
                          >
                            {/* ── V2 Services section ── */}
                            {section.id === "services" && v2ServiceOptions ? (
                              <>
                                <div className="relative text-gray-600 mb-3">
                                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                                    <MagnifyingGlassIcon aria-hidden="true" className="h-4 w-4" />
                                  </div>
                                  <input
                                    type="search"
                                    placeholder="Search"
                                    value={v2SearchQuery}
                                    onChange={(e) => setV2SearchQuery(e.target.value)}
                                    className="block w-full rounded-md border bg-white py-1.5 pl-7 pr-3 focus:bg-white focus:text-gray-900 focus:ring-0 focus:placeholder:text-gray-500 sm:text-sm sm:leading-6"
                                  />
                                </div>
                                <div className="space-y-2.5 pb-1">
                                  {filteredV2Options.map((option, optionIdx) => {
                                    if (option.isGroupHeader) {
                                      const groupMembers = option.groupMembers || [];
                                      const selectedInGroup = groupMembers.filter((m) => selectedIds.services?.includes(v2NameToId[m]));
                                      const allSelected = groupMembers.length > 0 && selectedInGroup.length === groupMembers.length;
                                      const someSelected = selectedInGroup.length > 0 && !allSelected;
                                      return (
                                        <div key={`d-grp-${optionIdx}`} className="pt-3 pb-1 first:pt-0">
                                          <span
                                            className={`text-[11px] font-semibold uppercase tracking-wider select-none cursor-pointer transition-colors ${allSelected || someSelected ? "text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}
                                            onClick={() => handleV2CheckboxChange(option.value, !allSelected)}
                                          >
                                            {option.label}
                                          </span>
                                        </div>
                                      );
                                    }
                                    const showNew = isNewServiceCategory(option.value);
                                    const indentPx = (option.depth || 0) * 16;
                                    const isChecked = selectedIds.services?.includes(option.rfiId) || false;
                                    return (
                                      <div key={`d-sc-${optionIdx}`} className="flex items-start gap-3" style={{ paddingLeft: `${indentPx}px` }}>
                                        <input
                                          value={option.value}
                                          checked={isChecked}
                                          onChange={(e) => handleV2CheckboxChange(option.value, e.target.checked)}
                                          type="checkbox"
                                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mt-0.5"
                                        />
                                        <label className="text-sm font-medium text-gray-900 leading-relaxed cursor-pointer flex-1">
                                          {option.label}
                                          {showNew && (
                                            <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-violet-600 ring-1 ring-inset ring-violet-400/60 align-middle">
                                              New
                                            </span>
                                          )}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            ) : (
                              /* ── Default section rendering ── */
                              <>
                            {(section.name === "Vendor" ||
                              section.name === "Services" ||
                              section.name === "Scope" ||
                              section.name === "Auditor" ||
                              section.name === "Sponsor" ||
                              section.name === "Templates") && (
                              <div className="mb-2">
                                <Searchbar
                                  placeholder="Search here..."
                                  isFilterSearch={false}
                                  isSearchIcon={true}
                                  width="w-full"
                                  marginRight="mr-2"
                                  mainSearch={true}
                                  toolbar={true}
                                  onChange={handleSearch} // Trigger search on input change
                                  handleClearSearch={handleClearSearch}
                                />
                              </div>
                            )}

                            <form className="space-y-4">
                              {section?.options?.map((option, optionIdx) => (
                                <div
                                  key={`${section?.id ?? "sec"}-${optionIdx}-${
                                    option?.value ?? option?.statusID ?? option?.id ?? "opt"
                                  }`}
                                  className="flex items-start gap-3"
                                >
                                  <input
                                    defaultValue={
                                      option.value ||
                                      option?.id ||
                                      option?.rfi_id ||
                                      option?.hash_id
                                    }
                                    checked={isFilterOptionChecked({
                                      currentPath,
                                      sectionId: section.id,
                                      selectedIds,
                                      option,
                                    })}
                                    id={`filter-${section.id}-${optionIdx}`}
                                    name={`${section.id}[]`}
                                    type="checkbox"
                                    onChange={handleCheckboxChange(
                                      ...getFilterCheckboxHandlerArgs(option)
                                    )}
                                    className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <label
                                    htmlFor={`filter-${section.id}-${optionIdx}`}
                                    className="min-w-0 flex-1 break-words text-sm font-medium text-gray-900 cursor-pointer"
                                  >
                                    <CustomTooltip
                                      cutOffAfter={30}
                                      sliceLength={30}
                                    >
                                      {option.label ||
                                        option?.name ||
                                        option?.rfi_name ||
                                        option?.vqa_auditor_name ||
                                        option?.label}
                                    </CustomTooltip>
                                  </label>
                                </div>
                              ))}
                            </form>
                              </>
                            )}
                          </PopoverPanel>
                        </>
                      )}
                    </Popover>
                  ))}
                  {extraDesktopFilters ? (
                    <div className="flex items-center border-l border-gray-200 divide-x divide-gray-200">
                      {extraDesktopFilters}
                    </div>
                  ) : null}
                </PopoverGroup>
              </div>
            </div>
          </div>
        </div>

        {/* Active filters */}
      </section>
    </div>
  );
}
